import {
  useCallback,
  useRef,
  useState,
  useMemo,
  type HTMLAttributes,
  useEffect,
  Fragment,
  type ComponentProps,
} from 'react';
import { css } from '@emotion/react';
import DOMPurify from 'dompurify';
import { $getRoot } from 'lexical';
import type { TeamChatTypes } from '@frontend/api-team-chat';
import { GifPicker, StreamGiphyType } from '@frontend/gif-picker';
import { CalloutDialog } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { MediaUploadPreview } from '@frontend/media-upload-preview';
import { useThrottledFn } from '@frontend/timer';
import { theme } from '@frontend/theme';
import {
  RTEditor,
  Button,
  useAlert,
  useModalControl,
  usePopoverDialog,
  DynamicFieldPlugin,
  Text,
  useTooltip,
} from '@frontend/design-system';
import { USERS_SEARCH_DELAY } from '../../constants';
import { useFilesUpload, UpdatedThreadSendingMediaItem } from '../../hooks/use-files-upload';
import { useUserSearch } from '../../hooks/use-user-search';
import { useActiveConversationSelector } from '../../providers/active-conversation.provider/active-conversation.provider';
import { useTeamChatSelector } from '../../providers/team-chat.provider';
import { getMentionsFromText, getUserFullName, type Target } from '../../utils';
import { ImageUploadModal, type ImageUploadModalHandle } from './image-upload-modal';
import { MentionMenu } from './mentions/mention-menu';
import { MentionMenuItem } from './mentions/mention-menu-item';
import { SubmitButton } from './submit-button';
import { TypingIndicator } from './typing-indicator';

interface MessageComposerEditProps {
  type: 'edit';
  onCancel: () => void;
  message: TeamChatTypes.Message;
}

interface MessageComposerNewProps {
  type: 'new';
  selectedTarget?: Target;
}

type MessageComposerProps = { className?: string } & (MessageComposerEditProps | MessageComposerNewProps);

export const MessageComposer = (props: MessageComposerProps) => {
  const { t } = useTranslation('team-chat');
  const alert = useAlert();
  const { onSearchUsers, filteredAllUsers } = useUserSearch();

  const activeConversationId = useTeamChatSelector((ctx) => ctx.activeConversationId);
  const conversations = useTeamChatSelector((ctx) => ctx.conversations);
  const createConversation = useTeamChatSelector((ctx) => ctx.createConversation);
  const createConversationError = useTeamChatSelector((ctx) => ctx.createConversationError);
  const helpers = useTeamChatSelector((ctx) => ctx.helpers);
  const isThread = useTeamChatSelector((ctx) => ctx.isThread);
  const openStaticConversation = useTeamChatSelector((ctx) => ctx.openStaticConversation);
  const mainPanelView = useTeamChatSelector((ctx) => ctx.mainPanelView);
  const selectedThreadParentId = useTeamChatSelector((ctx) => ctx.selectedThreadParentId);
  const users = useTeamChatSelector((ctx) => ctx.users);
  const shouldFetchConversationUsers = useTeamChatSelector((ctx) => ctx.shouldFetchConversationUsers);
  const pendingComposerText = useTeamChatSelector((ctx) => ctx.pendingComposerText);
  const clearPendingComposerText = useTeamChatSelector((ctx) => ctx.clearPendingComposerText);
  const hideEnhancedAttachmentsBanner = useTeamChatSelector((ctx) => ctx.hideEnhancedAttachmentsBanner);
  const isEnhancedAttachmentsEnabled = useTeamChatSelector((ctx) => ctx.isEnhancedAttachmentsEnabled);
  const { setNoShowEnhancedAttachmentsBanner } = useTeamChatSelector((ctx) => ctx.cache);

  const editMessage = useActiveConversationSelector((ctx) => ctx.editMessage);
  const editMessageError = useActiveConversationSelector((ctx) => ctx.editMessageError);
  const sendConversationEvent = useActiveConversationSelector((ctx) => ctx.sendConversationEvent);
  const sendMessage = useActiveConversationSelector((ctx) => ctx.sendMessage);
  const sendMessageError = useActiveConversationSelector((ctx) => ctx.sendMessageError);
  const uploadAttachment = useActiveConversationSelector((ctx) => ctx.uploadAttachment);
  const uploadAttachmentError = useActiveConversationSelector((ctx) => ctx.uploadAttachmentError);
  const members = useActiveConversationSelector((ctx) => ctx.members);

  const mentionsMenuOpenRef = useRef(false);
  const typingStopRef = useRef<ReturnType<typeof setTimeout>>();

  const onCancel = props.type === 'edit' ? props.onCancel : undefined;
  const existingMessage = props.type === 'edit' ? props.message : undefined;
  const selectedTarget = props.type === 'new' ? props.selectedTarget : undefined;
  const isEditing = !!existingMessage;
  const showCalloutBanner = isEnhancedAttachmentsEnabled && !hideEnhancedAttachmentsBanner && !isEditing;

  const imageUploadModalControl = useModalControl();
  const imageUploadModalRef = useRef<ImageUploadModalHandle>(null);
  const { hideUploadingFilesEffect, files, removeFile, setFiles, showUploadingFilesEffect, addGif } =
    useFilesUpload(isEnhancedAttachmentsEnabled);
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({});

  const {
    getDialogProps: getFileSharingPromoDialogProps,
    refs: fileSharingPromoRefs,
    open: openBanner,
  } = usePopoverDialog({
    placement: 'top',
    middlewareOptions: {
      offset: { mainAxis: 8, alignmentAxis: 0 },
    },
    interactionOptions: {
      dismiss: {
        outsidePress: false,
        escapeKey: false,
      },
    },
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hideEnhancedAttachmentsBanner) openBanner();
    }, 500);

    return () => clearTimeout(timer);
  }, [openBanner, hideEnhancedAttachmentsBanner]);

  // Seed the initial value from a message staged by `openConversationWithUser`. Doing this in the
  // lazy initializer (rather than only in an effect) guarantees the very first RTE mount already has
  // the prefilled `initialHTML`, so an existing-conversation open (whose view switch is async) can't
  // race the composer mounting before the effect applies the text.
  const [value, setValue] = useState<string>(
    () => existingMessage?.text || (props.type === 'new' && pendingComposerText) || ''
  );

  // i don't think we want to show a loading state for every sending message. i havent seen other chat apps do this
  // const _isLoading = isCreatingConversation || isUploadingAttachment || isSendingMessage || isEditingMessage;
  const error = createConversationError || uploadAttachmentError || sendMessageError || editMessageError;

  //TODO: the RTE editor doesn't expose a hook that allows us to control the html from the outside (that I can find)
  //So we'll use a key to clear the editor state when we want to
  const [key, setKey] = useState('key-' + Math.random());

  const { users: RTEUsers } = useMemo(
    () => getRTEMentionItems(filteredAllUsers ?? [], conversations?.groups ?? []),
    [filteredAllUsers, conversations]
  );

  const conversation = useMemo(
    () => (activeConversationId ? helpers.getConversation(activeConversationId) : undefined),
    [activeConversationId, helpers]
  );

  // Check if this is a DM with a deactivated user
  const isDeactivatedUserDM = useMemo(() => {
    if (!conversation || conversation.type !== 'DM') return false;
    return members.every((user) => user.isDeactivated);
  }, [conversation, members]);

  const placeholder = useMemo(() => {
    if (conversation?.name && conversation.type === 'DM') {
      return t('Message {{name}}...', { name: conversation.name });
    } else if (conversation?.name && conversation.type === 'Group') {
      return t('Message #{{name}}...', { name: conversation.name });
    }
    return t('Use @ to mention others...');
  }, [conversation?.name, conversation?.type, t]);

  const reset = () => {
    onCancel?.();
    setValue('');
    setKey('key-' + Math.random());
  };

  const throttledSendTypingEvent = useThrottledFn(() => {
    if (activeConversationId) {
      if (typingStopRef.current) {
        clearTimeout(typingStopRef.current);
      }
      sendConversationEvent({
        conversationId: activeConversationId,
        event: 'typing_start',
      });
      typingStopRef.current = setTimeout(() => {
        sendConversationEvent({
          conversationId: activeConversationId,
          event: 'typing_stop',
        });
      }, 2000);
    }
  }, 500);

  const onMentionSearch = useCallback(
    async (_: string, searchText: string | null | undefined) => {
      const trimmedSearchText = searchText?.trim().toLowerCase();
      if (!trimmedSearchText) return RTEUsers['@'];

      const results = await onSearchUsers(trimmedSearchText);

      return results.map((user) => ({
        id: user.userID,
        label: `@${user.firstName} ${user.lastName}`,
        value: `${user.firstName} ${user.lastName}`,
        noTransform: false,
      }));
    },
    [onSearchUsers, RTEUsers]
  );

  const onChange = async (newValue: string) => {
    setValue(newValue);
    if (activeConversationId && newValue?.length > 1) {
      throttledSendTypingEvent();
    }
  };

  const onEditorChange: ComponentProps<typeof RTEditor>['onChange'] = (editor) => {
    editor.read(() => {
      const textContent = $getRoot().getTextContent();
      //We shouldn't need to communicate every keystroke to the parent... we just need it to know the final value when it is submitted
      onChange(textContent);
    });
  };

  const handleSubmit = useCallback(async () => {
    if (isDeactivatedUserDM) {
      return;
    }
    const hasFiles = !!files.length;
    const submitMessage = async (conversation: TeamChatTypes.Conversation) => {
      let attachments: TeamChatTypes.Attachment[] = [];
      if (hasFiles) {
        showUploadingFilesEffect();

        // Using reduce to minimize loops otherwise we will need to use filter and map together
        const { nonEmptyFiles, gifs } = files.reduce<{
          nonEmptyFiles: File[];
          gifs: UpdatedThreadSendingMediaItem[];
        }>(
          (acc, asset) => {
            if (!asset.uploadable && asset.meta) {
              acc.gifs.push(asset);
            } else if (asset && asset.file) {
              acc.nonEmptyFiles.push(asset.file);
            }
            return acc;
          },
          { nonEmptyFiles: [], gifs: [] }
        );

        attachments = await uploadAttachment({
          channelId: conversation.channelId,
          files: nonEmptyFiles,
        });

        if (gifs.length) {
          // @ts-expect-error FIXME: @MehulWeave update the types so that it accepts the correct type
          // Adding this back in because this is the type that giphy accepts on mobile side.
          attachments.push(...gifs.map((asset) => ({ type: 'giphy', ...(asset.meta as StreamGiphyType) })));
        }
      }
      const mentionedUserIds = getMentionsFromText(
        value,
        users?.map((user) => getUserFullName(user.firstName, user.lastName)) ?? []
      )
        .map((userName) => users?.find((user) => getUserFullName(user.firstName, user.lastName) === userName)?.userID)
        .filter((userId) => userId !== undefined);

      const message =
        props.type === 'edit' && existingMessage
          ? await editMessage({
              ...existingMessage,
              attachments: [...(existingMessage.attachments ?? []), ...attachments],
              text: DOMPurify.sanitize(value),
              mentionedUserIds: mentionedUserIds,
            })
          : await sendMessage({
              conversationId: conversation.channelId,
              message: {
                attachments,
                text: DOMPurify.sanitize(value),
                mentionedUserIds: mentionedUserIds,
                // it may be kinda sneaky to just pass this in here. the other option is to break this function out into pieces so that adding another branch of logic for `sendReply` would go here
                // TODO: jonathan butler will refactor to make this more clear
                parentMessageId: selectedThreadParentId,
              },
            });

      if (hasFiles) {
        hideUploadingFilesEffect();
        setFiles([]);
        isEnhancedAttachmentsEnabled && imageUploadModalRef.current?.resetFiles();
      }

      return message;
    };

    if (activeConversationId && conversation) {
      return submitMessage(conversation)
        .then(() => {
          reset();
          if (mainPanelView === 'conversation-dynamic' && activeConversationId) {
            // if we have a activeConversationId and the conversation mode is not static it means that we are still
            //  able to open other conversations with the search bar. But when we send a message while selecting a
            //  channel from search bar, the search bar should hide itself. This function does that
            openStaticConversation(activeConversationId);
          }
        })
        .catch(() => {
          alert.error(t('Failed to send message'));
        });
    }

    if (selectedTarget?.type === 'dm' && !activeConversationId) {
      const existingConversation = helpers.getConversationByMemberIds(selectedTarget.users.map((user) => user.userID));
      if (existingConversation) {
        openStaticConversation(existingConversation.channelId);
        reset();
        return;
      } else {
        return createConversation({
          type: 'dm',
          name: undefined,
          memberIds: selectedTarget.users.map((user) => user.userID),
        })
          .then((conversation) => {
            openStaticConversation(conversation.channelId);
            return conversation;
          })
          .then((conversation) => {
            return submitMessage(conversation);
          })
          .then((message) => {
            reset();
            return message;
          })
          .catch((error) => {
            alert.error(t('Failed to send message' + error.message));
          });
      }
    }
  }, [helpers, value, files, isEditing, mainPanelView, activeConversationId, isDeactivatedUserDM]);

  const handleKeyEvents: HTMLAttributes<HTMLFormElement>['onKeyDown'] = (e) => {
    if (e.key === 'Escape') {
      onCancel?.();
    }
    if (e.key === 'Enter' && !e.shiftKey && !mentionsMenuOpenRef.current && !!value.trim()) {
      e.preventDefault();
      e.stopPropagation(); //to stop the RTE editor from inserting a line break
      handleSubmit();
    }
  };

  const dynamicFieldProps = useMemo(() => {
    if (shouldFetchConversationUsers) {
      return {
        searchDelay: USERS_SEARCH_DELAY,
        onSearch: onMentionSearch,
        triggers: ['@'],
      };
    } else {
      return {
        defaultMentionItems: RTEUsers,
        trigger: '@',
      };
    }
  }, [RTEUsers, onMentionSearch, shouldFetchConversationUsers]);

  /**
   * TODO: An error in any of these cases should be handled in the UI, rather than triggering a toast.
   * There should be some UI in the composer that shows the error message gives the user some options on what to try.
   * Maybe a 'retry', 'reset', etc...
   */
  useEffect(() => {
    error && alert.error('Error: ' + ((error as Error)?.message ?? error?.toString()));
  }, [error]);

  useEffect(() => {
    // this is here to focus the message composer input when the channel changes
    // if we remove this, the input will not focus
    setKey('key-' + Math.random());
  }, [conversation?.channelId]);

  useEffect(() => {
    // Prefill a message staged by `openConversationWithUser` (only for a fresh/new composer).
    // Bump the key so the RTE editor remounts and picks up the new `initialHTML`.
    if (props.type !== 'new' || !pendingComposerText) {
      return;
    }
    setValue(pendingComposerText);
    setKey('key-' + Math.random());
    clearPendingComposerText();
  }, [pendingComposerText, props.type, clearPendingComposerText]);

  return (
    <form
      className={isEditing ? 'edit-message' : 'new-message'}
      css={[styles.wrapper(isEditing, isThread), props.className]}
      id='message-composer'
      onKeyDownCapture={handleKeyEvents}
      onSubmit={(e) => e.preventDefault()}
    >
      {isDeactivatedUserDM && (
        <Text color='disabled' size='medium' css={styles.deactivatedLabel}>
          {t('This user is no longer active. You cannot send new messages.')}
        </Text>
      )}
      {conversation && !isEditing && !selectedThreadParentId && (
        <TypingIndicator typingUserIds={conversation?.usersTyping ?? []} />
      )}
      {!!files.length && (
        <MediaUploadPreview
          media={files}
          removeImageTrackingId='team-chat-v2-remove-image-upload'
          removeMediaItem={removeFile}
          inPopout
        />
      )}
      {/* putting this key here to allow resetting the RTE editor easily. Putting it directly on the editor component throws an error*/}
      <Fragment key={key}>
        <div css={[editorStyles, isDeactivatedUserDM && styles.editorDisabled]}>
          <RTEditor
            initialHTML={value}
            onChange={onEditorChange}
            hasDraggableBlocks={false}
            hideFloatingToolbar={true}
            autoFocus={!!conversation?.channelId && !isDeactivatedUserDM}
            readOnly={isDeactivatedUserDM}
          >
            <RTEditor.Editor trigger={'@'} placeholder={placeholder} />
            <div
              css={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between',
                padding: theme.spacing(0.5),
              }}
            >
              <div css={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                <RTEditor.Toolbar.EmojiPicker showBoxShadow={false} showCaret={false} css={{ height: 40 }} />
                <GifPicker
                  trackingId='team-chat-2.0-gif-button-click'
                  onSelect={addGif}
                  disabled={isDeactivatedUserDM}
                />

                <div ref={fileSharingPromoRefs.setReference} css={styles.fileSharingPromoAnchor}>
                  <Button
                    {...(isEnhancedAttachmentsEnabled
                      ? { onClick: () => imageUploadModalRef.current?.openFilePicker(), ...triggerProps }
                      : imageUploadModalControl.triggerProps)}
                    css={css`
                      height: 40px !important;
                      width: 40px;
                      svg {
                        height: 24px;
                        width: 24px;
                      }
                    `}
                    variant='secondary'
                    iconName={isEnhancedAttachmentsEnabled ? 'attachment' : 'image'}
                    trackingId={
                      isEnhancedAttachmentsEnabled
                        ? 'team-chat-2.0-file-attachment-button-click'
                        : 'team-chat-2.0-image-button-click'
                    }
                    disabled={isDeactivatedUserDM}
                  />
                  {isEnhancedAttachmentsEnabled && (
                    <Tooltip {...tooltipProps}>
                      {t(
                        'Attach images and documents. Supports JPG, PNG, PDF, and DOCX files. Up to 10 files, 10 MB each.'
                      )}
                    </Tooltip>
                  )}
                  {showCalloutBanner && (
                    <CalloutDialog
                      color='seaweed'
                      popoverDialogProps={getFileSharingPromoDialogProps()}
                      badge='new'
                      title={
                        <Text weight='bold' size='medium' as='span'>
                          {t('More ways to share files in Team Chat')}
                        </Text>
                      }
                      cardStyles={{ width: '360px' }}
                      containerStyles={{ overflow: 'visible' }}
                      description={
                        <Text weight='regular' size='medium' as='span'>
                          {t(
                            'Team Chat is no longer limited to image sharing. You can now upload and share PDF and DOCx files directly in your conversations, making it easier to review, discuss, and collaborate on documents with your team.'
                          )}
                        </Text>
                      }
                      actions={
                        <Button
                          variant='tertiary'
                          onClick={setNoShowEnhancedAttachmentsBanner}
                          trackingId='team-chat-2.0-file-sharing-promo-dismiss'
                        >
                          {t('Dismiss')}
                        </Button>
                      }
                    />
                  )}
                </div>
              </div>
              <div css={styles.actionsBar}>
                {isEditing ? (
                  <div style={{ gap: theme.spacing(1), display: 'flex' }}>
                    <Button variant='secondary' onClick={onCancel}>
                      {t('Cancel')}
                    </Button>
                    <Button
                      variant='primary'
                      type='submit'
                      onClick={() => handleSubmit()}
                      disabled={isDeactivatedUserDM}
                    >
                      {t('Save')}
                    </Button>
                  </div>
                ) : (
                  <SubmitButton
                    disabled={(!value && !files.length) || isDeactivatedUserDM}
                    onClick={() => handleSubmit()}
                  />
                )}
              </div>
            </div>

            <DynamicFieldPlugin
              CustomMenu={MentionMenu}
              CustomMenuItem={MentionMenuItem}
              onMenuOpen={() => {
                mentionsMenuOpenRef.current = true;
              }}
              onMenuClose={() => {
                mentionsMenuOpenRef.current = false;
              }}
              {...dynamicFieldProps}
            />

            {/** TODO: eventually we will want to allow for selecting conversations from a dropdown
              <DynamicFieldPlugin
              trigger='#'
              defaultMentionItems={RTEConversations}
              CustomMenu={Menu}
              CustomMenuItem={MenuItem}
            /> */}
          </RTEditor>
        </div>
      </Fragment>

      <ImageUploadModal
        ref={imageUploadModalRef}
        modalProps={imageUploadModalControl.modalProps}
        isPDFandDocxAllowed={isEnhancedAttachmentsEnabled}
        onImageUpload={setFiles}
      />
    </form>
  );
};

MessageComposer.displayName = 'MessageComposer';

const getRTEMentionItems = (users: TeamChatTypes.User[], conversations: TeamChatTypes.Conversation[]) => {
  return {
    users: users?.length
      ? {
          ['@']: users.map((user) => ({
            id: user.userID,
            label: `@${user.firstName} ${user.lastName}`,
            value: `${user.firstName} ${user.lastName}`,
            noTransform: false,
          })),
        }
      : { '@': [] },
    conversations: conversations?.length
      ? {
          ['#']: conversations.map((c) => ({
            id: c.channelId,
            label: `#${c.name}`,
            value: `#${c.name}}`,
            noTransform: false,
          })),
        }
      : { '#': [] },
  };
};

const editorStyles = css`
  #message-composer {
    height: max-content !important;
  }
  .emoji-picker-action,
  .action-pressed-chip {
    height: 40px;
  }
  .editor-paragraph {
    font-size: 1rem !important;
    line-height: 1.5 !important;
    top: ${theme.spacing(0)} !important;
    left: ${theme.spacing(0)} !important;
  }
  .editor-scroller {
    resize: none !important;
  }
  .editor-scroller,
  .editor-input {
    min-height: 72px !important; //TODO: sorry about the importants but the styling in the editor is too strong. We need to loosen that up.
  }
  .editor-placeholder {
    /* TODO: the figma file actually shows these measurements, but they feel really weird, so I'm silently adjusting them here     
    top: ${theme.spacing(2.5)};
    left: ${theme.spacing(1)};
    */
    // using important to override styles on the RTE :(
    top: 9px !important;
    left: 18px !important;
    color: ${theme.colors.neutral30} !important;
  }
  .editor-container {
    margin: 0 !important;
  }
  .editor-input {
    /* padding: ${theme.spacing(2, 1)}; TODO: Another tiny adjustment */
    padding: ${theme.spacing(1, 2)} !important;
  }
  .dynamic-field {
    background: ${theme.colors.primary5};
    border-radius: ${theme.borderRadius.small};
    color: ${theme.colors.primary60};
    padding: 2px 2px;
  }
`;

const styles = {
  fileSharingPromoAnchor: css`
    display: flex;
  `,
  wrapper: (isEditing: boolean, isThread: boolean) => [
    isThread &&
      css`
        border-bottom: 1px solid ${theme.colors.neutral10};
      `,
    css`
      position: relative;
      border-top: 1px solid ${theme.colors.neutral10};
      // this ul is for the image preview
      > ul {
        height: 100px;
        padding: ${theme.spacing(1, 2)};
      }

      li {
        background-color: var(--fabric-color-neutral-alpha-100-alpha-5);
        border: none;
      }

      img {
        height: 100px;
        width: 100px;
        object-fit: cover;
      }

      .editor-inner,
      .editor-container {
        border-radius: ${theme.borderRadius.medium};
      }
      .editor-input {
        border-radius: ${theme.borderRadius.medium};
      }
      &.edit-message {
        form {
          border: 1px solid ${theme.colors.warning50};
          border-radius: ${theme.borderRadius.medium};
          background-color: ${theme.colors.white};

          height: max-content;

          > div:first-of-type {
            padding: ${theme.spacing(1, 1, 0)};
          }

          > div:last-of-type {
            background-color: ${theme.colors.white};
            padding: ${theme.spacing(0.75, 1)};
          }
        }
      }
    `,
    isEditing &&
      css`
        .editor-container {
          border: 1px solid ${theme.colors.warning50};
        }
      `,
  ],
  actionsBar: css`
    display: flex;
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    padding: ${theme.spacing(0.5)};
    margin: ${theme.spacing(0.5)};
    gap: ${theme.spacing(0.5)};
  `,
  deactivatedLabel: css`
    background-color: ${theme.colors.neutral10};
    border: 1px solid ${theme.colors.neutral30};
    border-radius: 4px;
    padding: ${theme.spacing(0, 0.5)};
  `,
  editorDisabled: css`
    pointer-events: none;
    opacity: 0.6;
  `,
};
